import { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { saveProduct } from '../services/productService';
import { Product } from '../utils/productUtils';

export const useProductForm = (
  onSuccess?: (product: Product) => void,
  onError?: (error: string) => void
) => {
  const { currentUser, effectiveScopeUid } = useAuth();
  const [formData, setFormData] = useState<Partial<Product> & { grossPrice?: number }>({
    name: '',
    description: '',
    netPrice: 0,
    grossPrice: 0,
    taxRate: 19,
    sellerArticleId: '',
    unitCode: 'C62'
  });
  const [isPriceGross, setIsPriceGross] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  
  // Produkt zum Bearbeiten laden
  const loadProductForEdit = useCallback((product: Product) => {
    setEditingProduct(product);
    const grossPrice = product.netPrice * (1 + product.taxRate / 100);
    setFormData({
      ...product,
      grossPrice
    });
  }, []);
  
  // Preismodus umschalten
  const togglePriceMode = useCallback(() => {
    setIsPriceGross(prev => !prev);
  }, []);
  
  // Formular zurücksetzen
  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      description: '',
      netPrice: 0,
      grossPrice: 0,
      taxRate: 19,
      sellerArticleId: '',
      unitCode: 'C62'
    });
    setEditingProduct(null);
    setIsPriceGross(false);
  }, []);
  
  // Formularfelder-Handler
  const handleFormChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'grossPrice' && isPriceGross) {
      const grossPrice = parseFloat(value) || 0;
      const taxRate = formData.taxRate || 0;
      const netPrice = grossPrice / (1 + taxRate / 100);
      
      setFormData(prev => ({
        ...prev,
        grossPrice,
        netPrice: parseFloat(netPrice.toFixed(2))
      }));
    } else if (name === 'netPrice' && !isPriceGross) {
      const netPrice = parseFloat(value) || 0;
      const taxRate = formData.taxRate || 0;
      const grossPrice = netPrice * (1 + taxRate / 100);
      
      setFormData(prev => ({
        ...prev,
        netPrice,
        grossPrice: parseFloat(grossPrice.toFixed(2))
      }));
    } else if (name === 'taxRate') {
      const taxRate = parseFloat(value) || 0;
      const currentPrice = isPriceGross ? (formData.grossPrice || 0) : (formData.netPrice || 0);
      
      if (isPriceGross) {
        const netPrice = currentPrice / (1 + taxRate / 100);
        setFormData(prev => ({ ...prev, taxRate, netPrice: parseFloat(netPrice.toFixed(2)) }));
      } else {
        const grossPrice = currentPrice * (1 + taxRate / 100);
        setFormData(prev => ({ ...prev, taxRate, grossPrice: parseFloat(grossPrice.toFixed(2)) }));
      }

    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  }, [isPriceGross]);
  
  // Form speichern
  const saveForm = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) return;
    
    setIsProcessing(true);
    
    try {
      // Wenn editingProduct existiert, nehmen wir dessen ID, sonst undefined (neues Produkt)
      const productId = editingProduct?.id;
      
      // Speichern des Produkts
      const savedProductId = await saveProduct(effectiveScopeUid, formData, productId);
      
      // Erfolgreiches Speichern
      if (onSuccess) {
        // Erstelle ein vollständiges Produkt-Objekt
        const savedProduct: Product = {
          ...formData,
          id: savedProductId,
          // Diese Felder wurden in der Datenbank aktualisiert und sollten hier auch aktualisiert werden
          createdAt: editingProduct?.createdAt || new Date(),
          updatedAt: new Date()
        } as Product;
        
        onSuccess(savedProduct);
      }
      
      resetForm();
      return true;
    } catch (err: any) {
      console.error('Fehler beim Speichern:', err);
      if (onError) {
        onError(`saveError:${err.message || 'unknownError'}`);
      }
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, [currentUser, effectiveScopeUid, editingProduct, formData, onSuccess, onError, resetForm]);
  
  return {
    formData,
    setFormData,
    isPriceGross,
    isProcessing,
    editingProduct,
    loadProductForEdit,
    handleFormChange,
    togglePriceMode,
    saveForm,
    resetForm
  };
}; 